import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export const CONCERN_REASONS = [
  "Item not as described",
  "Item damaged in transit",
  "Wrong item received",
  "Missing accessories that were listed",
  "Item not received",
];

// Leave-at-door concerns are only about the item itself (loss/theft/weather after the
// door photo are waived), so only these four reasons are offered.
export const LEAVE_AT_DOOR_REASONS = [
  "Wrong item delivered",
  "Arrived damaged",
  "Significantly not as described in the listing",
  "Missing items shown in the listing",
];

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const rd = new FileReader();
    rd.onload = () => resolve(rd.result);
    rd.onerror = () => reject(rd.error || new Error("Could not read file"));
    rd.readAsDataURL(file);
  });

export default function ConcernModal({ tid, leaveAtDoor, onClose, onDone }) {
  const reasons = leaveAtDoor ? LEAVE_AT_DOOR_REASONS : CONCERN_REASONS;
  const [reason, setReason] = useState(reasons[0]);
  useEffect(() => { setReason(reasons[0]); }, [leaveAtDoor]);
  const [details, setDetails] = useState("");
  // Keep the selected File objects; they are read to base64 at submit time so a fast
  // Submit click can never race the asynchronous FileReader and post an empty photo list.
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const add = (e) => setFiles(Array.from(e.target.files || []).slice(0, 3));
  const submit = async () => {
    setBusy(true);
    try {
      const photos = await Promise.all(files.map(readAsDataUrl));
      await api.post(`/transactions/${tid}/raise-concern`, { reason, details, photos });
      toast.success("Concern submitted — payment frozen while we review");
      onClose(); onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!tid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="concern-modal">
        <DialogHeader><DialogTitle>Raise a concern</DialogTitle></DialogHeader>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger data-testid="concern-reason"><SelectValue /></SelectTrigger>
          <SelectContent>{reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea data-testid="concern-details" placeholder="Additional details (optional)" value={details} onChange={(e) => setDetails(e.target.value)} className="rounded-lg" />
        <div>
          <p className="mb-1 text-xs text-slate-500">Upload up to 3 photos as evidence (optional)</p>
          <input type="file" accept="image/*" multiple onChange={add} data-testid="concern-photos" className="text-sm" />
          {files.length > 0 && <p className="mt-1 text-xs text-slate-500" data-testid="concern-photo-count">{files.length} photo(s) attached</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-full bg-red-600 hover:bg-red-700" data-testid="concern-submit" onClick={submit} disabled={busy}>{busy ? "Submitting…" : "Submit concern"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
